import { useCallback, useEffect, useId, useState } from 'react';
import { type AssessmentSummary, STATE_LABELS } from '@zevik/schemas';
import type { Source } from '../api/client';
import { hrefFor } from '../api/href';
import {
  createAssessment,
  createProject,
  listAssessments,
  listProjects,
  type Project,
} from '../api/pipeline';
import type { Me } from '../credential/api';
import { apiSentence, sessionLost } from '../credential/session';
import { Masthead } from './Masthead';
import { Refused } from './credential/CredentialShell';

/**
 * Where a signed-in person lands: every assessment the tenant has, and a way
 * to start one (docs/DECISIONS.md ZD-120 §2, ZD-124 §2).
 *
 * The list is `GET /v1/assessments` joined to `GET /v1/projects` for the
 * project's name; the state shown is the read model's, in `STATE_LABELS`'
 * words rather than the machine's (ZD-124 §9), and is for finding an
 * assessment, not for acting on it — the assessment page reads `next` before
 * it offers anything. Every assessment is shown, closed ones with their
 * state, because the population is the table and nothing hides a row.
 *
 * **A new tenant sees the one screen with nothing to render.** It says so
 * — no assessments yet — and the form to make one is the screen, since that
 * is the only thing a person can do here.
 *
 * Making one is two requests where the project is new (`POST /v1/projects`,
 * then `POST /v1/assessments`) and one where it is not. Every control is
 * offered to every role and a refusal is the API's sentence: the workspace
 * records decisions the same way, and a role rule in the bundle would be a
 * second copy of the one the routes hold.
 */

const NEW_PROJECT = '';

export function Landing({
  source,
  me,
  onSignOut,
  onSessionLost,
}: {
  source: Source;
  me: Me | null;
  onSignOut: () => void;
  onSessionLost: (reason: string) => void;
}): React.JSX.Element {
  const [assessments, setAssessments] = useState<AssessmentSummary[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([listAssessments(source), listProjects(source)])
      .then(([listed, known]) => {
        if (!live) return;
        setAssessments(listed);
        setProjects(known);
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (sessionLost(error)) onSessionLost(apiSentence(error));
        else setFailure(error instanceof Error ? error.message : String(error));
      });
    return () => {
      live = false;
    };
  }, [source, onSessionLost]);

  const projectName = (id: string): string =>
    projects?.find((project) => project.project_id === id)?.name ??
    'a project this list cannot name';

  return (
    <div className="shell">
      <Masthead subject="Assessments" chip={null} me={me} onSignOut={onSignOut} home />
      <main className="landing">
        {failure !== null ? (
          <Refused message={failure} />
        ) : assessments === null || projects === null ? (
          <p className="landing__lead">Reading the assessments…</p>
        ) : assessments.length === 0 ? (
          <p className="landing__lead">
            There are no assessments yet. Start one below: name it, name the project it belongs to,
            and the next page takes the documents.
          </p>
        ) : (
          <table className="assessments">
            <thead>
              <tr>
                <th scope="col">Assessment</th>
                <th scope="col">Project</th>
                <th scope="col">Where it stands</th>
                <th scope="col">Last moved</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((assessment) => (
                <tr key={assessment.assessment_id}>
                  <td>
                    <a href={hrefFor(window.location.search, assessment.assessment_id)}>
                      {assessment.title}
                    </a>
                  </td>
                  <td>{projectName(assessment.project_id)}</td>
                  <td className="assessments__state">{STATE_LABELS[assessment.state]}</td>
                  <td>
                    <time dateTime={assessment.updated_at}>
                      {new Date(assessment.updated_at).toLocaleString()}
                    </time>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {projects === null ? null : (
          <NewAssessment
            source={source}
            projects={projects}
            onSessionLost={onSessionLost}
            onCreated={(assessmentId) => {
              window.location.assign(hrefFor(window.location.search, assessmentId));
            }}
          />
        )}
      </main>
    </div>
  );
}

function NewAssessment({
  source,
  projects,
  onCreated,
  onSessionLost,
}: {
  source: Source;
  projects: Project[];
  onCreated: (assessmentId: string) => void;
  onSessionLost: (reason: string) => void;
}): React.JSX.Element {
  const titleId = useId();
  const projectId = useId();
  const newProjectId = useId();
  const [title, setTitle] = useState('');
  const [project, setProject] = useState<string>(projects[0]?.project_id ?? NEW_PROJECT);
  const [newProject, setNewProject] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = useCallback(async (): Promise<void> => {
    setRefusal(null);
    setSending(true);
    try {
      const chosen =
        project === NEW_PROJECT
          ? (await createProject(source, newProject.trim())).project_id
          : project;
      const created = await createAssessment(source, { project_id: chosen, title: title.trim() });
      onCreated(created.assessment_id);
    } catch (error) {
      if (sessionLost(error)) onSessionLost(apiSentence(error));
      else setRefusal(apiSentence(error));
    } finally {
      setSending(false);
    }
  }, [source, project, newProject, title, onCreated, onSessionLost]);

  return (
    <form
      className="newassessment"
      aria-label="New assessment"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2>New assessment</h2>
      <label className="field" htmlFor={titleId}>
        Title
      </label>
      <input
        id={titleId}
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
        autoComplete="off"
      />
      <label className="field" htmlFor={projectId}>
        Project
      </label>
      <select
        id={projectId}
        value={project}
        onChange={(event) => {
          setProject(event.target.value);
        }}
      >
        {projects.map((known) => (
          <option key={known.project_id} value={known.project_id}>
            {known.name}
          </option>
        ))}
        <option value={NEW_PROJECT}>A new project…</option>
      </select>
      {project === NEW_PROJECT ? (
        <>
          <label className="field" htmlFor={newProjectId}>
            Project name
          </label>
          <input
            id={newProjectId}
            value={newProject}
            onChange={(event) => {
              setNewProject(event.target.value);
            }}
            autoComplete="off"
          />
        </>
      ) : null}
      {refusal === null ? null : <Refused message={refusal} />}
      <div className="newassessment__actions">
        <button type="submit" className="submit" disabled={sending}>
          {sending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}
